/**
 * AMB-222 follow-up — central helper for "what name should I render
 * here when the input is null / undefined / blank".
 *
 * The BE-side `UserDisplayNameResolver` already guarantees that every
 * response touching a user-or-master surface carries a non-empty name
 * (MasterProfile.PublishedName > DraftName > AppUser.DisplayName >
 * "User"), so these client-side fallbacks fire only when:
 *   - the caller built a synthetic item (e.g. mapping a calendar row
 *     where the BE didn't include the master);
 *   - the BE response is from an older endpoint that hasn't been wired
 *     through the resolver yet;
 *   - the user is anonymous / no auth at all.
 *
 * Three constants because the right fallback is context-dependent:
 *   - `FALLBACK_MASTER` for surfaces that present someone in their
 *     master/host capacity (cards, calendar, room/live host name).
 *   - `FALLBACK_PARTICIPANT` for «другой человек на странице мастера»
 *     (their followers, related users).
 *   - `FALLBACK_GUEST` for chat-message authors whose user record
 *     couldn't be resolved (rare; usually a deleted account leaving
 *     a stranded message).
 *
 * Why a function over inline `||`: a single grep target if the fallback
 * copy ever needs to change, and a clear hook for future i18n (today
 * the strings are hardcoded Russian because the rest of the user-facing
 * copy is Russian-only).
 */

export const FALLBACK_MASTER = 'Мастер';
export const FALLBACK_PARTICIPANT = 'Участник';
export const FALLBACK_GUEST = 'Гость';

/**
 * Returns the trimmed name when non-empty, otherwise `fallback`.
 *
 * For chained candidates (`a ?? b ?? c`) just inline the chain at the
 * call site: `resolveName(a ?? b ?? c)`. The helper itself stays
 * single-argument so the trimming/empty rule lives in one place.
 */
export function resolveName(
  name: string | null | undefined,
  fallback: string = FALLBACK_MASTER,
): string {
  if (typeof name !== 'string') return fallback;
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

/** Minimal shape of the authenticated user needed to resolve their name. */
export interface SelfNameSource {
  /**
   * Canonical name from `/auth/me` (BE `UserDisplayNameResolver`:
   * MasterProfile.PublishedName ?? DraftName ?? AppUser.DisplayName).
   */
  resolvedDisplayName?: string | null;
  /** Editable personal name (AppUser.DisplayName). Master-agnostic. */
  displayName?: string | null;
}

/**
 * CORE-764 — the name to render on the *logged-in user's own* surfaces
 * (their profile header, share sheet, settings summary).
 *
 * A user with a master profile whose master name differs from their
 * personal `AppUser.DisplayName` was shown under two names: the header /
 * menu / Live read `resolvedDisplayName` (master-first), while the profile
 * header read `getPublicUserProfile().displayName` — which the BE fills
 * with the *personal* name only (`ChatDisplayNameFactory`, master profile
 * ignored). Same account, two names.
 *
 * This helper is the single client-side source of truth for that decision:
 * `resolvedDisplayName` always wins, so a master shows their master name
 * everywhere. `displayName` and the optional `publicProfileName`
 * (`getPublicUserProfile().displayName`) are last-resort fallbacks for the
 * frame before `/auth/me` has populated `resolvedDisplayName`, or for a
 * synthetic caller that never loaded the auth user.
 *
 * The true fix for *other users'* profiles lives in the BE
 * (`PublicUserProfileController` should resolve via
 * `UserDisplayNameResolver`, not `ChatDisplayNameFactory`); this helper
 * only unifies the surfaces the client can see for its own user.
 */
export function resolveSelfDisplayName(
  user: SelfNameSource | null | undefined,
  publicProfileName?: string | null,
): string {
  const candidates = [user?.resolvedDisplayName, user?.displayName, publicProfileName];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return '';
}
